import { createFeatureSelector, createSelector } from '@ngrx/store';

import { State } from '@app/stp/state/reducers';
import { PolicyState } from '@app/stp/state/reducers/policy.reducer';

import { Vehicle } from '@global/infrastructure/interfaces/vehicle';
import { Result } from '@app/features/premium/models/result';

import { ChangeTypeEnum } from '@global/infrastructure/enums/change-type.enum';

import { BreakdownCoverMap, DlgCoverCodeMap } from '@global/infrastructure/constants/premium';
import { PaymentBillingIdEnum } from '@global/infrastructure/enums/payments.enum';
import { Utility } from '@global/infrastructure/helpers/utility';
import { selectPolicyQuoteRetrievedStatus } from './stp.selectors';


const selectStpStore = createFeatureSelector<State>('stpStore');

export const selectPolicyState = createSelector(
  selectStpStore,
  (state: State) => state?.policyState
);

export const selectLoadPolicyError = createSelector(
  selectPolicyState,
  state => state.initialState.error
);

export const selectPolicyDetails = createSelector(
  selectPolicyState,
  (state: PolicyState) => state?.initialState?.policyDetails
);

export const selectVerifyPolicyDetails = createSelector(
  selectPolicyState,
  (state: PolicyState) => state.initialState.policyDetailsVerify
);

export const selectInitialPolicyDetails = createSelector(
  selectPolicyDetails,
  state => state?.result
);

export const selectLatestPremium = createSelector(
  selectPolicyState,
  ({requestPayload: {premium}}) => premium
);

export const selectInitialDrivers = (changeType: ChangeTypeEnum) =>
  createSelector(selectInitialPolicyDetails, (state) =>
    (state?.lobData?.mOTLine_Ext?.coverables?.drivers || []).filter(
      (d) => d?.driverType === changeType
    )
  );

export const selectInitialFixedIds = createSelector(
  selectInitialPolicyDetails,
  (state) =>
    state?.lobData?.mOTLine_Ext?.coverables?.drivers?.map((d) => d.fixedId)
);

export const selectSource = createSelector(
  selectInitialPolicyDetails,
  state => state?.baseData?.source_dlg as string
);

const mapToVehicleDisplay = (
  policyDetails: any,
  latestPremium,
  typeOfVehicle: 'permanent' | 'temporary',
  isQuoteRetrieved = false
): Vehicle => {
  let motVehicles;
  if (isQuoteRetrieved) {
    motVehicles = latestPremium?.lobData?.mOTLine_Ext?.coverables?.motVehicles;
  } else {
    motVehicles =
      policyDetails?.result?.lobData?.mOTLine_Ext?.coverables?.motVehicles;
  }

  const currentVehicle = motVehicles.filter(
    (m) => m.vehicle.typeOfVehicle === typeOfVehicle
  )[0];
  if (currentVehicle) {
    const {
      make,
      model,
      registrationNumber,
      transmission,
      fuelType,
      year,
      engineSize,
      bodyType,
    } = currentVehicle.vehicle;
    return {
      make,
      model,
      registrationNumber,
      transmission,
      fuelType,
      year,
      engineSize,
      bodyType,
    };
  }

  return null;
};

export const selectExistingPermanentVehicleDetails = createSelector(
  selectPolicyDetails,
  selectLatestPremium,
  selectPolicyQuoteRetrievedStatus,
  (policyDetails, latestPremium, isQuoteRetrieved) =>
    mapToVehicleDisplay(
      policyDetails,
      latestPremium,
      ChangeTypeEnum.Permanent,
      isQuoteRetrieved
    )
);

export const selectExistingTemporaryVehicleDetails = createSelector(
  selectPolicyDetails,
  selectLatestPremium,
  selectPolicyQuoteRetrievedStatus,
  (policyDetails, latestPremium, isQuoteRetrieved) =>
    mapToVehicleDisplay(
      policyDetails,
      latestPremium,
      ChangeTypeEnum.Temporary,
      isQuoteRetrieved
    )
);

export const selectExistingDriversInPolicy = (changeType: ChangeTypeEnum) => createSelector(
  selectPolicyState,
  ({requestPayload: {premium: {lobData: {mOTLine_Ext: {coverables}}}}}) => {
    const {drivers, motVehicles, vehicleDrivers} = coverables;
    const permVehicle = motVehicles.filter(v => v.vehicle.typeOfVehicle === ChangeTypeEnum.Permanent)[0];
    const tempVehicle = motVehicles.filter(v => v.vehicle.typeOfVehicle === ChangeTypeEnum.Temporary)[0];

    const mainDriverId = changeType === ChangeTypeEnum.Temporary && !!tempVehicle ?
      vehicleDrivers.filter(vd => vd.vehicleID === tempVehicle.vehicle.publicID).length > 0 ?
        vehicleDrivers.filter(vd => vd.vehicleID === tempVehicle.vehicle.publicID)[0].mainDriverID :
        vehicleDrivers.filter(vd => vd.vehicleID === permVehicle.vehicle.publicID)[0].mainDriverID :
      vehicleDrivers.filter(vd => vd.vehicleID === permVehicle.vehicle.publicID)[0].mainDriverID;

    return {
      drivers: drivers.filter(d => !d.tempDigitalId_dlg),
      mainDriverId,
      ncdOwnerId: permVehicle.ncdOwnerInUse
    };
  }
);

export const selectPermanentDrivers = createSelector(
  selectPolicyState,
  ({requestPayload: {premium: {lobData: {mOTLine_Ext: {coverables}}}}}) => ({
    drivers: coverables.drivers.filter(d => !d.tempDigitalId_dlg && d.driverType?.toLowerCase() === ChangeTypeEnum.Permanent)
  })
);

export const selectLicences = createSelector(
  selectPolicyState,
  selectInitialPolicyDetails,
  (updated, initial) => {
    const updatedDrivers =
      updated?.requestPayload?.premium?.lobData?.mOTLine_Ext?.coverables
        ?.drivers || [];
    const initialDrivers =
      initial?.lobData?.mOTLine_Ext?.coverables?.drivers || [];

    return {
      updatedDrivers: updatedDrivers.map((d) => ({
        id: d.fixedId,
        licenceType: d.typeOfDrivingLicense,
      })),
      initialDrivers: initialDrivers.map((d) => ({
        id: d.fixedId,
        licenceType: d.typeOfDrivingLicense,
      })),
    };
  }
);

export const selectPolicyHolder = createSelector(
  selectPolicyDetails,
  ({result: {lobData: {mOTLine_Ext: {coverables}}}}) => coverables.drivers.filter(d => d.isPolicyHolder)[0]
);

export const selectPremiumForReview = (occupations) => createSelector(
  selectLatestPremium,
  premium => ({occupations, premium})
);

export const selectBindData = createSelector(
  selectLatestPremium,
  (state) => state.bindData
);

export const selectBaseData = createSelector(
  selectLatestPremium,
  (state) => state.baseData
);

export const selectPeriodStartDate = createSelector(
  selectLatestPremium,
  ({baseData: {periodStart}}) => periodStart
);

export const selectAccountHolder = createSelector(
  selectBaseData,
  (state) => state.accountHolder
);

export const selectPolicyStateChanges = createSelector(
  selectPolicyState,
  (state) => state.changes
);

export const selectAccountHolderMaritalStatus = createSelector(
  selectAccountHolder,
  selectPolicyStateChanges,
  (accountHolderState, changesState) => {
    return changesState?.yourDetails?.maritalStatus ? changesState.yourDetails.maritalStatus : accountHolderState?.maritalStatus;
  }
);

export const selectedPaymentPlan = createSelector(
  selectBindData,
  (state) => state.selectedPaymentPlan
);

export const selectPremiumCoverLevel = createSelector(
  selectLatestPremium,
  premium => {
    const {
      bindData: {chosenQuote: chosenQuoteId},
      quoteData: {offeredQuotes},
      lobData: {mOTLine_Ext: {offerings, coverables: {motVehicles}}}
    } = premium;

    const {branchCode} = offeredQuotes.filter(q => q.publicID === chosenQuoteId)[0];
    const {coverages: {vehicleCoverages}} = offerings.filter(o => o.periodPublicId_dlg === chosenQuoteId)[0];
    const rescueExtras = (vehicleCoverages[0].rescueCoverages_dlg || []).filter(r => r.selected)
      .map(r => BreakdownCoverMap[r.covName] !== undefined ? BreakdownCoverMap[r.covName] : DlgCoverCodeMap[r.covName]);

    return {
      coverLevelKey: branchCode,
      optionalExtrasRescue: [...rescueExtras],
      vehicleEndorsements: motVehicles[0].vehicle.vehicleEndorsement
    };
  }
);

export const selectIsPCW = createSelector(selectInitialPolicyDetails, (state) =>
  Utility.isPCW(state)
);

export const selectPremiumBindData = createSelector(
  selectLatestPremium,
  (state: Result) => state.bindData
);

const selectPolicyStateErrors = createSelector(
  selectPolicyState,
  state => state.errors
);

export const selectGatewayTimeoutError = createSelector(
  selectPolicyStateErrors,
  error => error?.isGatewayTimeout
);

export const selectPromoCodeError = createSelector(
  selectPolicyStateErrors,
  error => error?.isInvalidPromoCode
);

export const selectPermVehicleRegistration = createSelector(
  selectPolicyDetails,
  ({result: {lobData: {mOTLine_Ext: {coverables: {motVehicles}}}}}) => {
    return motVehicles.filter(({vehicle}) => vehicle.typeOfVehicle === ChangeTypeEnum.Permanent)[0]?.vehicle.registrationNumber;
  }
);

export const selectPremiumForIssueChange = createSelector(
  selectPolicyState,
  state => {
    if (state.finalState?.boundPolicy) {
      return {hasPolicyBound: true, result: state.finalState.boundPolicy};
    }
    if (!state.finalState.isNewDirectDebit) {
      return {hasPolicyBound: false, result: state.requestPayload.premium};
    }

    const {requestPayload: {premium}, changes} = state;
    if (!changes?.payment) {
      return {hasPolicyBound: false, result: state.requestPayload.premium};
    }
    const {paymentDetails} = changes.payment;
    return {
      hasPolicyBound: false,
      result: {
        ...premium,
        bindData: {
          ...premium.bindData,
          directDebitDetails_dlg: paymentDetails,
          selectedPaymentMethod: 'DirectDebit',
          selectedPaymentPlan: PaymentBillingIdEnum.PayMonthly,
          renewThisVersionInd_dlg: true,
          recvRenewalNoticeInd_dlg: true,
          autoRenewInd_dlg: paymentDetails.isAutoRenewal
        }
      }

    };

  }
);


// TODO: revisit the default allocation syntax
export const selectTemporaryDriversForAllocate = createSelector(
  selectPolicyState,
  ({
     changes: {
       temporaryDriver: {newDrivers = [], removedDrivers = []} = {
         newDrivers: [],
         removedDrivers: []
       }
     }, requestPayload: {premium: {lobData: {mOTLine_Ext: {coverables}}}}
   }) => ([
    ...newDrivers,
    ...coverables.drivers.filter(d =>
      d.driverType?.toLowerCase() === ChangeTypeEnum.Temporary && removedDrivers.findIndex(id => d.publicID === id) < 0
    )
  ])
);

export const selectNewTemporaryVehicleForAllocate = createSelector(
  selectPolicyState,
  ({changes: {temporaryCar}}) => temporaryCar
);

export const selectInvalidNextStartDateError = createSelector(
  selectPolicyState,
  ({errors: {invalidNextStartDate}}) => invalidNextStartDate
);

export const selectInitialCarDetails = (changeType: ChangeTypeEnum) =>
  createSelector(
    selectLatestPremium,
    (premium) => {
      const {
        lobData: {
          mOTLine_Ext: {
            coverables: { motVehicles },
          },
        },
      } = premium;
      return motVehicles.filter(
        (m) => m.vehicle.typeOfVehicle === changeType
      )[0]?.vehicle;
    }
  );

export const selectDvlaSearchParams = createSelector(
  selectLatestPremium,
  premium => {
    const {
      lobData: {mOTLine_Ext: {coverables: {motVehicles}}},
      baseData: {policyAddress: {postalCode}, periodStart: periodStartDate, termNumber_dlg: tenure},
      quoteRef_dlg: quoteRefId
    } = premium;
    const vrm = motVehicles.filter(v => v.vehicle.typeOfVehicle === ChangeTypeEnum.Permanent)[0]?.vehicle.registrationNumber;

    return {
      vrm,
      postalCode,
      periodStartDate,
      quoteRefId,
      tenure
    };
  }
);

export const selectRequestPayloadError = createSelector(
  selectPolicyState,
  ({requestPayload: {error}}) => !!error
);


export const selectAutoRenewalUserSelection = createSelector(
  selectPolicyState,
  state => state.changes?.renewal
);

export const selectIsPolicyLoaded = createSelector(
  selectPolicyState,
  (state: PolicyState) => state.initialState.isLoaded
);

/*
export const selectNewDirectDebitResolverPayload = createSelector(
  selectPolicyState,
  state => {
    const {requestPayload: {premium}, changes} = state;
    if (!changes?.payment) {
      return null;
    }
    const {paymentDetails} = changes.payment;
    return {
      ...premium,
      bindData: {
        ...premium.bindData,
        directDebitDetails_dlg: paymentDetails,
        selectedPaymentMethod: 'DirectDebit',
        selectedPaymentPlan: PaymentBillingIdEnum.PayMonthly,
        renewThisVersionInd_dlg: true,
        recvRenewalNoticeInd_dlg: true,
        autoRenewInd_dlg: paymentDetails.isAutoRenewal
      }
    };
  }
);
*/
